import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { gql } from '@apollo/client';
import _ from 'lodash';
import * as yup from 'yup';
import { Set } from 'immutable';
import { captureEvent } from '../../utils/analytics';
import { useRecord, useUserRole } from '../../utils/hooks';
import SidebarForm from './SidebarForm';
import {
  inputFields,
  operationInputFields,
} from './OperationsForm/OperationInputs';
import {
  DeleteFarmCropOperationMutation,
  DeleteTemplateOperationMutation,
  UpsertCropTemplateMutation,
  UpsertFarmCropMutation,
} from '../../mutations';
import { N_YEARS } from '../../constants/years';
import { nullableNumber, transformNaNTo } from '../../utils/yup';
import { isSpecialRevenueStream } from '../../constants/revenueStreams';
import LineItemsTable from '../tables/LineItemsTable';
import { ShrinkContent, ShrinkContentColumn } from '../ShrinkContent';
import CropOperationsForm from './OperationsForm/CropOperationsForm';
import {
  bulkEditFields,
  defaultOperation,
  fields,
  getOperationCsvUnitsMap,
  getOperationUnitSpecMap,
  lossEventFields,
  revenueStreamFields,
} from '../../constants/operations';
import {
  operationCsvCols,
  operationInputCsvCols,
  OPS_COLS,
  OPS_SUPERADMIN_COLS,
} from '../../constants/operationsCols';
import { asQuantityWithUnit } from '../../utils/formatters';
import { applyBulkEditsToObject, mergeObjects } from '../../utils/bulkEdits';
import { searchBy } from '../../utils/search';
import { sortOperations } from '../../utils/operations';
import {
  SidebarEditContext,
  SidebarEditContextProvider,
} from '../../contexts/SidebarEditContext';
import useCropExportFileName from '../../hooks/useCropExportFileName';
import { useUnitsMap } from '../../contexts/UnitContext';

const SUCCESS_MESSAGE = 'Operation changes saved successfully';

const queryString = `
  ${fields.join(' ')}
  lossEvents {
    ${lossEventFields.join(' ')}
  }
  revenueStreams {
    ${revenueStreamFields.join(' ')}
  }
  operationInputs {
    ${operationInputFields.join(' ')}
    input {
      ${inputFields.join(' ')}
    }
  }
  capexGroups {
    id
    capexes {
      id
      name
      requiresFuel
      gallonsFuelPerHour
      isImplement
    }
  }
`;

const TemplateOperationsQuery = gql`
  query TemplateOperations($id: ID!) {
    cropTemplate(id: $id) {
      id
      updatedAt
      name
      team {
        id
      }
      yield {
        id
        unit
      }
      lossEvents {
        ${lossEventFields.join(' ')}
      }
      revenueStreams {
        ${revenueStreamFields.join(' ')}
      }
      operations {
        ${queryString}
        calculationExplanation
        notes
      }
    }
  }
`;

const RawCropOperationsQuery = gql`
  query RawCropOperations($id: ID!) {
    rawCrop(id: $id) {
      id
      updatedAt
      name
      yield {
        id
        unit
      }
      lossEvents {
        ${lossEventFields.join(' ')}
      }
      revenueStreams {
        ${revenueStreamFields.join(' ')}
      }
      operations {
        ${queryString}
      }
    }
  }
`;

const yearsSchema = (schema) =>
  yup.object().shape({
    ...{
      ...Array.from({ length: N_YEARS }, () => schema),
    },
  });
const operationInputSchema = yup.object().shape({
  id: yup.string(),
  input: yup.object().shape({
    id: yup.string().required('Required'),
    name: yup.string().required('Required'),
  }),
  amountPerUnit: yup.number().min(0, 'Minimum is 0').required('Required'),
  unitForAmount: yup.string().required('Required'),
});

const capexGroupSchema = yup
  .object()
  .test({
    test: ({ capexes }) =>
      capexes != null &&
      // main must be defined + have a value
      capexes.main != null &&
      // implement can be undefined or have a value
      capexes.implement !== null,
    message: 'Cannot save with empty equipment',
  })
  .test({
    test: ({ capexes }) =>
      (!capexes?.implement?.isImplement && !capexes?.main?.isImplement) ||
      (capexes?.implement?.isImplement && capexes?.main?.requiresFuel),
    message: 'An implement must be attached to fueled equipment',
  })
  .test({
    test: ({ capexes }) =>
      capexes?.implement === undefined || capexes?.implement?.isImplement,
    message: 'The second piece of equipment in a group must be an implement',
  })
  .test({
    test: ({ capexes }) =>
      capexes?.main?.requiresFuel === false ||
      capexes?.main?.gallonsFuelPerHour != null,
    message: 'First piece of equipment is invalid, update to specify fuel use.',
  })
  .test({
    test: ({ capexes }) =>
      capexes?.main?.isImplement != null &&
      (capexes?.implement === undefined ||
        capexes?.implement?.isImplement != null),
    message:
      'At least 1 piece of equipment is invalid, please update before saving',
  });

const durationSchema = yup
  .number()
  .typeError('Must be formatted hr:min:sec')
  .min(0);

const operationSchema = yup.object().shape(
  {
    name: yup.string().required('Name is required'),
    category: yup.string().required('Category is required'),
    group: yup.string(),
    assignment: yup.string().required('Assignment is required'),
    millisecondsPerUnit: durationSchema.when(['rented', 'unitForDollars'], {
      is: (rented, unitForDollars) => rented || !unitForDollars,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
    unitForMilliseconds: yup.string().required('Required'),
    millisecondsOverrides: yearsSchema(durationSchema.nullable()).nullable(),
    numberOfLaborers: yup.number().min(0, 'Minimum is 0').required('Required'),
    lossEvents: yup
      .array()
      .nullable()
      .test({
        test: (schema) =>
          !(schema || []).some(({ percentTreeLoss }) => !percentTreeLoss),
        message: 'Cannot allocate to a non "Tree Loss" type loss event',
      }),
    revenueStreams: yup
      .array()
      .nullable()
      .test({
        test: (schema) =>
          !(schema?.revenueStreams || []).some(({ name }) =>
            isSpecialRevenueStream(name),
          ),
        message: 'Cannot allocate to this sales channel',
      }),
    contracted: yup.boolean(),
    rented: yup.boolean(),
    dollarsPerUnit: yup
      .number()
      // Resolve NaN type issue for null values
      .transform(transformNaNTo(null))
      .when(['contracted', 'unitForDollars'], {
        is: (contracted, unitForDollars) => contracted || !!unitForDollars,
        then: (schema) => schema.min(0, 'Minimum is 0').required('Required'),
        otherwise: (schema) => schema.nullable(),
      }),
    unitForDollars: yup.string().when('contracted', {
      is: true,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
    equipmentName: yup.array(yup.string().required('Required')).nullable(),
    equipmentRentalCostPerDay: yup
      .number()
      .nullable()
      .when('rented', {
        is: true,
        then: (schema) => schema.required('Required').min(0, 'Minimum is 0'),
        otherwise: (schema) => schema.nullable(),
      }),
    startingYear: nullableNumber()
      .positive('Must be positive')
      .integer('Must be a whole number')
      .max(N_YEARS, `Must be ${N_YEARS} or less`)
      .when('years', {
        is: (years) => !years?.length,
        then: (schema) =>
          schema.required('Starting year or custom years must be specified'),
        otherwise: (schema) => schema.nullable(),
      }),
    endingYear: nullableNumber()
      .positive('Must be positive')
      .integer('Must be a whole number')
      .max(N_YEARS, `Must be ${N_YEARS} or less`)
      .when('startingYear', {
        is: (startingYear) => startingYear == null,
        then: (schema) =>
          schema.test({
            name: 'is-null',
            message: 'No ending year without starting year',
            test: (value) => value == null,
          }),
        otherwise: (schema) =>
          schema.min(
            yup.ref('startingYear'),
            "Ending year can't be before starting year",
          ),
      }),
    cadence: nullableNumber()
      .positive('Must be positive')
      .integer('Must be a whole number')
      .when('startingYear', {
        is: (startingYear) => startingYear != null,
        then: (schema) => schema.required('Required'),
      }),
    timesPerYear: yup
      .number()
      .positive('Must be positive')
      .integer('Must be a whole number')
      .required('Required'),
    years: yup
      .string()
      .nullable()
      .test({
        test: (value) => value == null || /^(\d+(,\d+)*)?$/.test(value),
        message: 'Must be a comma-separated list of positive integers',
      }),
    capexGroups: yup
      .array(capexGroupSchema)
      .nullable()
      .test({
        test: (capexGroups) => {
          const allIds = _.flatMap(capexGroups, ({ capexes }) => [
            capexes.main?.id,
            capexes.implement?.id,
          ]).filter((id) => id != null);
          return allIds.length === _.uniq(allIds).length;
        },
        message: 'Cannot use the same equipment twice on one operation',
      }),
    operationInputs: yup.array(operationInputSchema).nullable(),
    calculationExplanation: yup.string().nullable(),
    notes: yup.string().nullable(),
  },
  // Eliminates circular dependency of these fields
  [['years', 'startingYear']],
);

const transformBulkEdits = ({ operations, ...values }) => {
  if (!operations) return { ...values };
  return {
    ...values,
    operations: operations.map((op) =>
      applyBulkEditsToObject(values, op, bulkEditFields),
    ),
  };
};

const bulkOperationSchema = yup
  .object()
  // Apply bulk edits to each selected operation
  .transform(transformBulkEdits)
  .shape({
    operations: yup.array().of(operationSchema).nullable(),
  });

const transformMillisecondsOverrides = (millisecondsOverrides) => {
  if (!millisecondsOverrides) return {};
  return (
    Object.entries(millisecondsOverrides)
      // Filter falsy values, except for zero
      .filter(([, duration]) => duration === 0 || duration)
      // Force values to integers in case they
      // were converted from another unit.
      .reduce((overrides, [year, duration]) => {
        return { ...overrides, [year]: Math.ceil(duration) };
      }, {})
  );
};

const transformCapexGroups = (capexGroups) => {
  if (!capexGroups) {
    return null;
  }

  return capexGroups.map(({ id, capexes }) => {
    const toAdd = { id, ids: [], resourceIds: [] };
    const addCapex = (capex) => {
      if (capex != null) {
        // eslint-disable-next-line no-underscore-dangle
        if (capex.__typename === 'CapexResource') {
          toAdd.resourceIds.push(capex.id);
        } else {
          toAdd.ids.push(capex.id);
        }
      }
    };
    addCapex(capexes.main);
    addCapex(capexes.implement);
    return toAdd;
  });
};

const transformOperationInput = (operationInput) => {
  const { input } = operationInput;

  return {
    ...operationInput,
    localId: undefined,
    input: undefined,
    // eslint-disable-next-line no-underscore-dangle
    inputId: input.__typename === 'RawInput' ? input.id : undefined,
    // eslint-disable-next-line no-underscore-dangle
    resourceId: input.__typename === 'InputResource' ? input.id : undefined,
    __typename: undefined,
  };
};

const transformValues = (values, { id, updatedAt, team }) => ({
  id,
  updatedAt,
  teamId: team?.id,
  operations: (values.operations || [values]).map((operation) => ({
    ...operation,
    equipmentName: operation.equipmentName?.join(',') || null,
    years: operation.years?.length
      ? operation.years?.split(',').map((year) => parseInt(year, 10))
      : null,
    revenueStreamIds:
      operation.revenueStreams?.map(({ id: rsId }) => rsId) || null,
    revenueStreams: undefined,
    lossEventIds: operation.lossEvents?.map(({ id: leId }) => leId) || null,
    lossEvents: undefined,
    operationInputs: (operation.operationInputs || []).map(
      transformOperationInput,
    ),
    // Force millisecondsPerUnit to be an integer in case it
    // was converted from another unit, but allow falsy values
    millisecondsPerUnit:
      operation.millisecondsPerUnit && Math.ceil(operation.millisecondsPerUnit),
    millisecondsOverrides: transformMillisecondsOverrides(
      operation.millisecondsOverrides,
    ),
    capexGroups: transformCapexGroups(operation.capexGroups),
    valid: undefined,
    __typename: undefined,
  })),
});

const transformOpForForm = (op) => {
  if (op == null) {
    return null;
  }
  const capexGroups = op.capexGroups.map(({ capexes, ...group }) => {
    if (capexes?.length === 1) {
      return { ...group, capexes: { main: capexes[0] } };
    }
    if (capexes?.length === 2) {
      const indexOfMain =
        capexes.findIndex((capex) => capex?.requiresFuel) || 0;
      const indexOfImplement = indexOfMain === 0 ? 1 : 0;
      return {
        ...group,
        capexes: {
          main: capexes[indexOfMain],
          implement: capexes[indexOfImplement],
        },
      };
    }
    return { ...group, capexes: {} };
  });
  // Represent years as a string in the form.
  const years = op.years?.join(',');
  const equipmentName = op.equipmentName
    ? op.equipmentName.split(',').filter((e) => e !== '')
    : undefined;
  return {
    ...op,
    capexGroups,
    years,
    equipmentName,
  };
};

const CropOperations = ({
  showNotes = false,
  onSubmit,
  onDeleteWithVars,
  resetMutationState,
  msg,
  resource,
  status,
}) => {
  const { resetEditState } = useContext(SidebarEditContext);
  const navigate = useNavigate();
  const setEditing = (op) => {
    if (op == null) {
      navigate('.');
    } else if (Set.isSet(op)) {
      if (op.size === 0) {
        navigate('.');
      } else if (op.size === 1) {
        navigate(op.first());
      } else {
        navigate('edit');
      }
    } else {
      navigate(op.id == null ? 'new' : op.id);
    }
  };
  const { isSuperadmin } = useUserRole();
  const { operationId } = useParams();
  const [selectedItems, setSelectedItems] = useState(new Set());

  const csvFileName = useCropExportFileName({
    cropName: resource?.name,
    title: 'Operation',
  });

  const operationUnitSpecMap = getOperationUnitSpecMap(resource?.yield?.unit);
  const operationUnitsMap = useUnitsMap(operationUnitSpecMap);
  const operationCsvUnitsMap = getOperationCsvUnitsMap(operationUnitsMap);

  // Remove path param if selected items are empty
  useEffect(() => {
    if (operationId === 'edit' && !selectedItems?.size) {
      navigate('.');
    }
  });

  // Reset the operation edit state when the operationId changes
  useEffect(() => {
    resetEditState();
    // Clear selected items if operationId is null
    if (operationId == null) setSelectedItems(new Set());
  }, [operationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const transformBulkOpsForForm = useCallback(() => {
    // Return null unless at least one operation is selected
    if (!selectedItems?.size) return null;
    const transformedOps = [...selectedItems]
      .map((opId) => resource?.operations?.find((op) => op.id === opId))
      .filter((op) => op) // Remove null ops resulting from invalid opIds
      .map((op) => transformOpForForm(op));
    return {
      ...mergeObjects(transformedOps, bulkEditFields),
      operations: transformedOps,
    };
  }, [resource, selectedItems]);

  useEffect(() => {
    if (selectedItems.size) {
      captureEvent('multiple-ops-selected', { count: selectedItems.size });
    }
  }, [selectedItems.size]);

  const transformedEditing = useMemo(() => {
    if (operationId === 'new') {
      return defaultOperation;
    }
    if (operationId === 'edit') {
      return transformBulkOpsForForm();
    }
    return transformOpForForm(
      resource?.operations?.find((op) => op.id === operationId),
    );
  }, [resource, operationId, transformBulkOpsForForm]);

  const { loading } = status;

  const getItems = (itemData) => {
    const { operations } = itemData;
    return sortOperations(operations).map((op) => ({
      ...op,
      displayEquipment:
        op.contracted || op.rented
          ? op.equipmentName
          : op.capexGroups
              ?.map((capexGroup) =>
                capexGroup.capexes.map((capex) => capex.name),
              )
              .join(','),
      onClick: () => {
        if (transformedEditing?.id === op.id) {
          setEditing(null);
        } else {
          setEditing(op);
        }
      },
    }));
  };

  const validationSchema = transformedEditing?.operations
    ? bulkOperationSchema
    : operationSchema;

  const maxOpInputs = Math.max(
    0, // Avoid -Infinity if there are no operations with inputs
    ...(resource?.operations || []).map((op) => op.operationInputs.length),
  );
  const csvCols = [
    ...operationCsvCols(operationCsvUnitsMap),
    ...operationInputCsvCols(maxOpInputs, operationCsvUnitsMap),
  ];

  return (
    <ShrinkContentColumn>
      <ShrinkContent className="d-flex flex-row no-wrap position-relative">
        <LineItemsTable
          data={resource}
          msg={msg}
          cols={isSuperadmin ? OPS_SUPERADMIN_COLS : OPS_COLS}
          getItems={getItems}
          createButton="New Operation"
          createButtonOnClick={() => {
            resetMutationState();
            setEditing(defaultOperation);
          }}
          csvButtonInToolbar
          csvButtonProps={{ cols: csvCols }}
          csvFileName={csvFileName}
          searchable
          search={searchBy(['name'])}
          filterableColumn="category"
          selectedIds={
            transformedEditing &&
            (transformedEditing.operations
              ? transformedEditing.operations.map(({ id }) => id)
              : [transformedEditing.id])
          }
          multiselectButton={() =>
            `Edit ${asQuantityWithUnit(
              'Operations',
              'Operation',
            )(selectedItems.size)}`
          }
          multiselectOnClick={() => {
            setEditing(selectedItems);
            captureEvent('multiple-ops-edit-clicked', {
              count: selectedItems.size,
            });
          }}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
        />
        {transformedEditing && (
          <SidebarForm
            size="lg"
            editing={transformedEditing}
            onClose={() => setEditing(null)}
            loading={loading}
            validationSchema={validationSchema}
            onSubmit={(...params) => {
              if (transformedEditing.operations) {
                captureEvent('multiple-ops-saved', {
                  count: transformedEditing.operations.length,
                });
              }
              return onSubmit(
                validationSchema,
                SUCCESS_MESSAGE,
              )(...params).then((value) => {
                // Sometimes transformedEditing does not change on submit, which
                // means that formik does not reinitialize the form.
                // Reinitializing the form is important because it clears the
                // dirty state. Clear the state manually to unblock navigation
                resetEditState();
                return value;
              });
            }}
            onDelete={onDeleteWithVars({
              operationId: transformedEditing?.id,
              operationIds: transformedEditing?.operations?.map(({ id }) => id),
            })}
            showDelete={
              transformedEditing?.id || transformedEditing?.operations
            }
            confirmEditText="Your operation has unsaved changes."
            validationErrorText="Your operation has validation errors that need to be addressed."
            status={status}
          >
            <CropOperationsForm
              showNotes={showNotes}
              resource={resource}
              editing={transformedEditing}
            />
          </SidebarForm>
        )}
      </ShrinkContent>
    </ShrinkContentColumn>
  );
};

export const TemplateCropOperations = () => {
  const props = useRecord({
    submitMutation: UpsertCropTemplateMutation,
    deleteMutation: DeleteTemplateOperationMutation,
    fetchRecordQuery: TemplateOperationsQuery,
    onDeleteRefetchQuery: TemplateOperationsQuery,
    transformValues,
  });
  return (
    <SidebarEditContextProvider>
      <CropOperations showNotes {...props} />
    </SidebarEditContextProvider>
  );
};

export const FarmCropOperations = () => {
  const props = useRecord({
    submitMutation: UpsertFarmCropMutation,
    deleteMutation: DeleteFarmCropOperationMutation,
    fetchRecordQuery: RawCropOperationsQuery,
    onDeleteRefetchQuery: RawCropOperationsQuery,
    transformValues,
    includeFarmId: true,
  });
  return (
    <SidebarEditContextProvider>
      <CropOperations {...props} />
    </SidebarEditContextProvider>
  );
};
